import type { TClaimMessage } from '@/@types/rcm';
import type { TTreatmentResponse } from '@/modules/claims/freeClaims/insuranceExport/types';

export interface ClaimTabMessage {
  readonly type: TClaimMessage['type'];
  readonly text: string;
}

export interface ClaimTabRow {
  readonly displayTitle: string;
  readonly treatmentKey: string;
  readonly txCode: string;
  readonly txDescription: string;
  readonly quadrant: TTreatmentResponse['quadrant'];
  readonly status: 'warning' | 'note' | 'clear';
  readonly messages: ClaimTabMessage[];
}

export interface ClaimTabData {
  readonly rows: ClaimTabRow[];
  readonly suggestedActions: string[];
  readonly treatmentCount: number;
  readonly warningCount: number;
}

function getRowStatus(messages: ClaimTabMessage[]): ClaimTabRow['status'] {
  if (messages.some((message) => message.type === 'warning')) {
    return 'warning';
  }
  if (messages.length > 0) {
    return 'note';
  }
  return 'clear';
}

export function buildClaimTabData(input: {
  treatments: TTreatmentResponse[];
  messages: TClaimMessage[];
}): ClaimTabData {
  const messagesByTreatmentId = new Map<number, ClaimTabMessage[]>();
  const suggestedActions: string[] = [];

  input.messages
    .filter((message) => message.type !== 'attachment_request')
    .forEach((message) => {
    if (typeof message.treatmentId === 'number') {
      const existingMessages =
        messagesByTreatmentId.get(message.treatmentId) ?? [];
      existingMessages.push({
        type: message.type,
        text: message.text,
      });
      messagesByTreatmentId.set(message.treatmentId, existingMessages);
      return;
    }
    if (!suggestedActions.includes(message.text)) {
      suggestedActions.push(message.text);
    }
    });

  const rows = input.treatments.map((treatment) => {
    const rowMessages = messagesByTreatmentId.get(treatment.txId) ?? [];

    return {
      displayTitle: treatment.txCode,
      treatmentKey: String(treatment.txId),
      txCode: treatment.txCode,
      txDescription: treatment.txDescription,
      quadrant: treatment.quadrant,
      status: getRowStatus(rowMessages),
      messages: rowMessages,
    };
  });

  return {
    rows,
    suggestedActions,
    treatmentCount: rows.length,
    warningCount: rows.filter((row) => row.status !== 'clear').length,
  };
}
